import { API_ENDPOINTS } from '../../../constants/api';
import { getWebAppOriginForStripeBillingRedirects } from '../../accounts/helpers/consumerBillingWebOrigin.helper';

type ApiClient = (endpoint: string, options?: Record<string, unknown>) => Promise<{ status: number; data: unknown }>;

export async function createCoinsCheckoutSession(
  apiClient: ApiClient,
  bundleId: string,
): Promise<{ url: string }> {
  const webAppOrigin = getWebAppOriginForStripeBillingRedirects();
  const response = await apiClient(API_ENDPOINTS.billing.coinsCheckoutSession, {
    method: 'POST',
    data: { bundleId, ...(webAppOrigin != null ? { webAppOrigin } : {}) },
  });
  const body = response.data as { success?: boolean; data?: { url?: string }; error?: string };
  if (response.status >= 400 || !body.success || !body.data?.url) {
    throw new Error(body.error ?? 'Failed to create checkout session');
  }
  return { url: body.data.url };
}

export async function createCoinsEmbeddedCheckoutSession(
  apiClient: ApiClient,
  bundleId: string,
): Promise<{ clientSecret: string; sessionId: string }> {
  const webAppOrigin = getWebAppOriginForStripeBillingRedirects();
  const response = await apiClient(API_ENDPOINTS.billing.coinsEmbeddedCheckoutSession, {
    method: 'POST',
    data: { bundleId, ...(webAppOrigin != null ? { webAppOrigin } : {}) },
  });
  const body = response.data as { success?: boolean; data?: { clientSecret?: string; sessionId?: string }; error?: string };
  if (response.status >= 400 || !body.success || !body.data?.clientSecret || !body.data?.sessionId) {
    throw new Error(body.error ?? 'Failed to create embedded checkout session');
  }
  return { clientSecret: body.data.clientSecret, sessionId: body.data.sessionId };
}

/**
 * Reconcile native (RevenueCat) coin purchases for the logged-in user. Credits any consumable purchases
 * held under `rcAppUserId` to the authenticated DB user. Idempotent server-side — safe to call repeatedly.
 * Returns the authoritative server balance.
 */
export async function syncCoinsRevenueCat(
  apiClient: ApiClient,
  rcAppUserId: string,
): Promise<{ coins: number; awarded: number }> {
  const response = await apiClient(API_ENDPOINTS.billing.coinsRevenueCatSync, {
    method: 'POST',
    data: { rcAppUserId },
  });
  const body = response.data as { success?: boolean; data?: { coins?: number; awarded?: number }; error?: string };
  if (response.status >= 400 || !body.success) {
    throw new Error(body.error ?? 'Failed to sync coins');
  }
  return {
    coins: body.data?.coins ?? 0,
    awarded: body.data?.awarded ?? 0,
  };
}

export async function syncCoinsCheckoutSession(
  apiClient: ApiClient,
  sessionId: string,
): Promise<{ coins: number; awarded: number }> {
  const response = await apiClient(API_ENDPOINTS.billing.coinsSyncCheckoutSession, {
    method: 'POST',
    data: { sessionId },
  });
  const body = response.data as { success?: boolean; data?: { coins?: number; awarded?: number }; error?: string };
  if (response.status >= 400 || !body.success) {
    throw new Error(body.error ?? 'Failed to sync checkout session');
  }
  return {
    coins: body.data?.coins ?? 0,
    awarded: body.data?.awarded ?? 0,
  };
}
